import { z } from 'zod'
import { i18n } from '@/i18n'
import { DEFAULT_PROJECT_FPS_OPTIONS, isAllowedProjectFps } from './project-fps'

/**
 * Validation schema for project creation/update form
 */
export function createProjectFormSchema(t: (key: string) => string) {
  return z.object({
    name: z
      .string()
      .min(1, t('projects.validation.nameRequired'))
      .max(100, t('projects.validation.nameTooLong'))
      .refine((name) => name.trim().length > 0, {
        message: t('projects.validation.nameWhitespace'),
      }),

    description: z
      .string()
      .max(500, t('projects.validation.descriptionTooLong'))
      .optional()
      .or(z.literal('')),

    width: z
      .number()
      .int(t('projects.validation.widthInteger'))
      .min(320, t('projects.validation.widthMin'))
      .max(7680, t('projects.validation.widthMax')),

    height: z
      .number()
      .int(t('projects.validation.heightInteger'))
      .min(240, t('projects.validation.heightMin'))
      .max(4320, t('projects.validation.heightMax')),

    fps: z
      .number()
      .int(t('projects.validation.fpsInteger'))
      .min(1, t('projects.validation.fpsMin'))
      .max(240, t('projects.validation.fpsMax'))
      .refine((fps) => isAllowedProjectFps(fps), {
        message: t('projects.validation.fpsUnsupported'),
      }),

    backgroundColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, t('projects.validation.invalidHexColor'))
      .optional(),
  })
}

export const projectFormSchema = createProjectFormSchema(i18n.t.bind(i18n))

/**
 * Type inferred from the schema
 */
export type ProjectFormData = z.infer<typeof projectFormSchema>

/**
 * Project template interface for preset configurations
 */
export interface ProjectTemplate {
  id: string
  platform: string
  name: string
  namePrefix: string
  width: number
  height: number
  fps: number
}

/**
 * Project resolution presets. Minimal, professional landscape formats for a
 * sports-analysis app (no social-media-targeted aspect ratios — use Custom for
 * anything else, e.g. vertical phone footage).
 */
export const PROJECT_TEMPLATES: readonly ProjectTemplate[] = [
  {
    id: 'landscape-1080p',
    platform: 'Landscape',
    name: '1080p',
    namePrefix: 'Project',
    width: 1920,
    height: 1080,
    fps: 30,
  },
  {
    id: 'landscape-4k',
    platform: 'Landscape',
    name: '4K UHD',
    namePrefix: 'Project',
    width: 3840,
    height: 2160,
    fps: 30,
  },
  {
    id: 'landscape-720p',
    platform: 'Landscape',
    name: '720p',
    namePrefix: 'Project',
    width: 1280,
    height: 720,
    fps: 30,
  },
] as const

/**
 * Common resolution presets
 * Updated for 2025 social media standards
 */
export const RESOLUTION_PRESETS = [
  // Landscape (16:9)
  { label: '1280×720 (HD)', value: '1280x720', width: 1280, height: 720 },
  { label: '1920×1080 (Full HD)', value: '1920x1080', width: 1920, height: 1080 },
  { label: '2560×1440 (2K)', value: '2560x1440', width: 2560, height: 1440 },
  { label: '3840×2160 (4K)', value: '3840x2160', width: 3840, height: 2160 },
  // Vertical (9:16) - TikTok, Reels, Shorts, Stories
  { label: '1080×1920 (TikTok / Reels / Shorts)', value: '1080x1920', width: 1080, height: 1920 },
  { label: '720×1280 (Vertical 720p)', value: '720x1280', width: 720, height: 1280 },
  // Square (1:1) - Instagram, Facebook, LinkedIn feeds
  { label: '1080×1080 (Square)', value: '1080x1080', width: 1080, height: 1080 },
  // Portrait (4:5) - Instagram feed optimal
  { label: '1080×1350 (Instagram Portrait)', value: '1080x1350', width: 1080, height: 1350 },
  // Ultrawide (21:9)
  { label: '2560×1080 (Ultrawide)', value: '2560x1080', width: 2560, height: 1080 },
] as const

/**
 * Common FPS presets
 */
export const FPS_PRESETS = [...DEFAULT_PROJECT_FPS_OPTIONS]

/**
 * Default form values
 */
export const DEFAULT_PROJECT_VALUES: ProjectFormData = {
  name: '',
  description: '',
  width: 1920,
  height: 1080,
  fps: 30,
}

/**
 * Get resolution aspect ratio
 */
export function getAspectRatio(width: number, height: number): string {
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b))
  const divisor = gcd(width, height)

  const ratioWidth = width / divisor
  const ratioHeight = height / divisor

  // Common aspect ratios
  if (ratioWidth === 16 && ratioHeight === 9) return '16:9'
  if (ratioWidth === 9 && ratioHeight === 16) return '9:16'
  if (ratioWidth === 4 && ratioHeight === 3) return '4:3'
  if (ratioWidth === 3 && ratioHeight === 4) return '3:4'
  if (ratioWidth === 21 && ratioHeight === 9) return '21:9'
  if (ratioWidth === 1 && ratioHeight === 1) return '1:1'
  if (ratioWidth === 2 && ratioHeight === 3) return '2:3'
  if (ratioWidth === 3 && ratioHeight === 2) return '3:2'
  if (ratioWidth === 4 && ratioHeight === 5) return '4:5'
  if (ratioWidth === 5 && ratioHeight === 4) return '5:4'

  return `${ratioWidth}:${ratioHeight}`
}
